import { PUBLIC_EVENTS_DATASETS } from "./events-datasets-Cu37u43g.js";
import { callEmbeddedAgent } from "./callEmbeddedAgent-ffwEeqdP.js";
import { createDatasetFieldsTool } from "./dataset-fields-CBauoBBr.js";
import { createOtelLookupTool } from "./otel-semantics-DGeu0HI-.js";
import { createWhoamiTool } from "./whoami-DU_KhHDm.js";
import { systemPrompt } from "./config-BqCCImAX.js";
import { createDatasetAttributesTool, createValidateEventsSearchTool } from "./utils-Dg2aeBEb.js";
import { z } from "zod";

//#region src/tools/support/search-events/agent.ts
const SEARCH_EVENTS_DATASETS = [...PUBLIC_EVENTS_DATASETS, "replays"];
const searchEventsAgentOutputSchema = z.object({
	dataset: z.enum(SEARCH_EVENTS_DATASETS).describe("Which dataset to use for the query"),
	query: z.string().describe("The Sentry query string for filtering results"),
	fields: z.array(z.string()).describe("Array of field names to return in results."),
	sort: z.string().describe("Sort parameter for results."),
	environment: z.union([z.string(), z.array(z.string()).min(1)]).nullable().default(null).describe("Separate environment filter for datasets like replays that do not support environment in the query string. Use a string for one environment or an array when multiple environments are requested."),
	timeRange: z.union([
		z.object({ statsPeriod: z.string().describe("Relative time period like '1h', '24h', '7d'") }),
		z.object({
			start: z.string().describe("ISO 8601 start time"),
			end: z.string().describe("ISO 8601 end time")
		}),
		z.null()
	]).describe("Time range for filtering events. Use either statsPeriod for relative time or start/end for absolute time."),
	explanation: z.string().default("").describe("Brief explanation of how you translated this query.")
}).refine((data) => {
	if (data.dataset === "replays") return true;
	if (!data.sort || !data.fields || data.fields.length === 0) return true;
	const sortField = data.sort.startsWith("-") ? data.sort.substring(1) : data.sort;
	return data.fields.includes(sortField);
}, { message: "Sort field must be included in the fields array. Sentry requires that any field used for sorting must also be explicitly selected. Add the sort field to the fields array or choose a different sort field that's already included." });
/**
* Search events agent - single entry point for translating natural language queries to Sentry search syntax
* This returns both the translated query result AND the tool calls made by the agent
*/
async function searchEventsAgent(options) {
	const datasetAttributesTool = createDatasetAttributesTool({
		apiService: options.apiService,
		organizationSlug: options.organizationSlug,
		projectId: options.projectId
	});
	const validateSearchTool = createValidateEventsSearchTool({
		apiService: options.apiService,
		organizationSlug: options.organizationSlug,
		projectId: options.projectId
	});
	const otelLookupTool = createOtelLookupTool({
		apiService: options.apiService,
		organizationSlug: options.organizationSlug,
		projectId: options.projectId
	});
	const replayFieldsTool = createDatasetFieldsTool({
		apiService: options.apiService,
		organizationSlug: options.organizationSlug,
		dataset: "replays",
		projectId: options.projectId
	});
	const whoamiTool = createWhoamiTool({ apiService: options.apiService });
	return await callEmbeddedAgent({
		system: systemPrompt,
		prompt: options.query,
		tools: {
			datasetAttributes: datasetAttributesTool,
			validateSearch: validateSearchTool,
			replayFields: replayFieldsTool,
			otelSemantics: otelLookupTool,
			whoami: whoamiTool
		},
		schema: searchEventsAgentOutputSchema
	});
}

//#endregion
export { searchEventsAgent, searchEventsAgentOutputSchema };
//# sourceMappingURL=agent-BCH9XRWB.js.map